import { useState, useEffect, useCallback } from 'react';
import { Database, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { isDemoEnvironment } from './EnvironmentBadge';

/**
 * Demo tab card: generate or remove the rich demo data set for one firm
 * (eight client companies, one per engagement stage, with populated grids,
 * messaging threads, change orders, tasks and portfolio history). Backed by
 * `/demo/rich-seed` on the server; demo environments only.
 */

interface SeededCompany {
  id: string;
  name: string;
  stage: string;
  stageLabel: string;
  engagementIds: string[];
  activeEngagementId: string | null;
  configItems: number;
  threads: number;
}

interface SeedStatus {
  seeded: boolean;
  seededAt: string | null;
  keyCount: number;
  companies: SeededCompany[];
  counts: Record<string, number> | null;
}

interface RichDemoDataCardProps {
  accessToken: string;
  /** System admins may target another firm; firm users seed their own. */
  firmId?: string;
  onComplete?: () => void;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/demo/rich-seed`;

export function RichDemoDataCard({ accessToken, firmId, onComplete }: RichDemoDataCardProps) {
  // Demo hosts only (staging and the *-demo subdomains), wherever the card is placed; the server checks again.
  const isDemo = isDemoEnvironment();
  const [status, setStatus] = useState<SeedStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState<'seed' | 'cleanup' | null>(null);

  const headersFor = (token: string) => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const url = firmId ? `${BASE}/status?firmId=${encodeURIComponent(firmId)}` : `${BASE}/status`;
      const res = await fetch(url, { headers: headersFor(accessToken) });
      if (res.ok) setStatus(await res.json());
    } catch (error) {
      console.error('[RICH_DEMO] status failed:', error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, firmId]);

  useEffect(() => { if (isDemo) loadStatus(); }, [loadStatus, isDemo]);

  if (!isDemo) return null;

  const run = async (kind: 'seed' | 'cleanup') => {
    const question = kind === 'seed'
      ? 'Generate rich demo data for this firm? Eight client companies at every engagement stage will be created (a previous run is replaced).'
      : 'Remove the rich demo data? Only the records the generator wrote are deleted.';
    if (!confirm(question)) return;
    try {
      setWorking(kind);
      const res = await fetch(kind === 'seed' ? BASE : `${BASE}/cleanup`, { method: 'POST', headers: headersFor(accessToken), body: JSON.stringify(firmId ? { firmId } : {}) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        toast.error(data.error || `Could not ${kind === 'seed' ? 'generate' : 'remove'} the rich demo data`);
        return;
      }
      if (kind === 'seed') {
        toast.success('Rich demo data generated', { description: `${data.companies?.length ?? 0} companies, ${data.records} records in ${Math.round((data.durationMs ?? 0) / 1000)}s`, duration: 6000 });
      } else {
        toast.success('Rich demo data removed', { description: `${data.deletedKeys} records deleted` });
      }
      await loadStatus();
      onComplete?.();
    } catch (error) {
      console.error('[RICH_DEMO] request failed:', error);
      toast.error('The request failed. Check the function logs.');
    } finally {
      setWorking(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="size-5" />
          Rich demo data (every engagement stage)
        </CardTitle>
        <CardDescription>
          Eight client companies for this firm, one per stage of the lifecycle: just created, mid-onboarding, early and late discovery,
          Ready for Build with change orders, in delivery, closed, and an archived engagement with a new one. Every grid, message thread,
          task and status update is real data in the live shapes, tagged so it can be removed without touching anything else.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => run('seed')} disabled={working !== null} className="gap-2">
            {working === 'seed' ? <Loader2 className="size-4 animate-spin" /> : <Database className="size-4" />}
            {status?.seeded ? 'Regenerate rich demo data' : 'Generate rich demo data'}
          </Button>
          <Button variant="destructive" onClick={() => run('cleanup')} disabled={working !== null || !status?.seeded} className="gap-2">
            {working === 'cleanup' ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
            Remove rich demo data
          </Button>
          <Button variant="ghost" size="sm" onClick={loadStatus} disabled={loading} className="gap-2">
            <RefreshCw className={`size-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {status?.seeded ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Seeded {status.seededAt ? new Date(status.seededAt).toLocaleString() : ''} · {status.keyCount} records
            </p>
            <ul className="divide-y rounded-md border">
              {status.companies.map((co) => (
                <li key={co.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium">{co.name}</span>
                    <span className="text-muted-foreground"> · {co.stageLabel}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline">{co.stage.replace(/_/g, ' ')}</Badge>
                    <span>{co.configItems} items</span>
                    <span>{co.threads} threads</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Nothing generated yet for this firm.</p>
        )}
      </CardContent>
    </Card>
  );
}
